// src/portal-app/tabs/InsightsTab.js
//
// P1 — Digest Scroll. Same pattern as OperationsTab.js (Task 5): a verdict
// sentence that states a comparison or nothing at all, an attention list
// that stays silent on a quiet period, and a dig-deeper list gated by
// useHubCards. See OperationsTab.js's header comment for the full pattern
// rationale — it is not re-derived here so the two comments cannot drift.
//
// The verdict rule (spec §5 P1, binding) reads data.revenueChangePct /
// data.grossMarginChangePts, BOTH of which are `null` whenever the previous
// period was zero (a real state for a new tenant or a quiet quarter) — that
// is NOT the same as 0 and must not be coerced into one. Four branches; the
// fourth is "no sentence at all" — never a status platitude ("Everything
// looks good"). VerdictCard already renders stats + sparkline alone for
// exactly that case — see buildVerdictSentence below.
import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useNav } from '../nav/NavStack';
import { useHubCards } from '../nav/useHubCards';
import {
    VerdictCard, AttentionList, CondensedRow, TabPills, ensureKitCss,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { fmtMoney } from '../insights/kit';
import { useInsightsData } from './data/useInsightsData';
import { ensureTabCss } from './tabCss';
import { HubEmptyState } from './HubEmptyState';
import { useRangePreset } from '../insights/rangeState';
import { PRESETS, rangeForPreset, formatRangeLabel } from '../nav/dateRange';

/* ---- local icon set — one per dig-deeper card, kept local to this screen
   per OperationsTab.js/TeamTab.js's precedent rather than growing the
   shared kit set for four consumers. Inline SVG only, never emoji
   (spec §4). ---- */
const IconFinancial = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 20V10M11 20V4M18 20v-7" />
    </svg>
);

const IconOperational = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3.2" />
        <path d="M19.4 13a7.6 7.6 0 0 0 0-2l2-1.5-2-3.5-2.4.7a7.6 7.6 0 0 0-1.7-1L15 3h-4l-.3 2.7a7.6 7.6 0 0 0-1.7 1l-2.4-.7-2 3.5L6.6 11a7.6 7.6 0 0 0 0 2l-2 1.5 2 3.5 2.4-.7a7.6 7.6 0 0 0 1.7 1L11 21h4l.3-2.7a7.6 7.6 0 0 0 1.7-1l2.4.7 2-3.5-2-1.5Z" />
    </svg>
);

const IconProduct = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3.5 8 12 3.5 20.5 8 12 12.5 3.5 8Z" />
        <path d="M3.5 8v8.5L12 21l8.5-4.5V8M12 12.5V21" />
    </svg>
);

const IconCost = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="8.5" />
        <path d="M14.5 9.3a2.6 2.6 0 0 0-2.4-1.5c-1.5 0-2.6 1-2.6 2.2s1 1.8 2.5 2.1c1.6.3 2.6.9 2.6 2.1s-1.1 2.2-2.6 2.2a2.6 2.6 0 0 1-2.5-1.5M12 6.5v1.3M12 16.2v1.3" />
    </svg>
);

const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    </svg>
);

const CARD_ICON = {
    financial: IconFinancial,
    operational: IconOperational,
    product: IconProduct,
    cost: IconCost,
};
const CARD_TONE = {
    financial: 'blue', operational: 'teal', product: 'purple', cost: 'amber',
};

/* Renders **bold** markers as real <strong> elements — the safe alternative
   to dangerouslySetInnerHTML. Matches HelpScreen.js's renderBold() and
   insights/kit.js's NarrativeCard: an established rule in this codebase,
   never reinvented per-screen beyond copying this same tiny helper. */
function renderBold(text) {
    const parts = String(text).split(/\*\*(.+?)\*\*/g);
    return parts.map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
}

// Verdict copy rule (spec §5 P1 — BINDING). Four branches, evaluated in
// order; the fourth is "no sentence at all" — never a platitude. `rev`/`pts`
// render as absolute magnitudes; direction is carried by the wording ("up" /
// "down" / "slipped" / "gained"), so a "down" sentence never also shows a
// minus sign.
function buildVerdictSentence(data, t) {
    const rev = data.revenueChangePct;
    const pts = data.grossMarginChangePts;

    if (rev != null && pts != null && rev > 0 && pts < 0) {
        return t(
            'verdict_insights_mixed',
            { rev: Math.abs(rev).toFixed(1), pts: Math.abs(pts).toFixed(1) },
            'Revenue is up {rev}%, but margin slipped {pts} points.',
        );
    }
    if (rev != null && pts != null && rev > 0 && pts > 0) {
        return t(
            'verdict_insights_up',
            { rev: Math.abs(rev).toFixed(1), pts: Math.abs(pts).toFixed(1) },
            'Revenue is up {rev}% and margin gained {pts} points.',
        );
    }
    if (rev != null && pts != null && rev < 0 && pts < 0) {
        return t(
            'verdict_insights_down',
            { rev: Math.abs(rev).toFixed(1), pts: Math.abs(pts).toFixed(1) },
            'Revenue is down {rev}% and margin slipped {pts} points.',
        );
    }
    // rev === 0 falls through to "no sentence" rather than this branch: an
    // "up 0.0%" statement is not a comparison or a consequence (the binding
    // verdict rule above), it is a platitude wearing a percentage sign
    // (Finding 5, final review).
    if (rev != null && rev !== 0) {
        const dir = rev >= 0 ? t('up', {}, 'up') : t('down', {}, 'down');
        return t(
            'verdict_insights_revenue_only',
            { rev: Math.abs(rev).toFixed(1), dir },
            'Revenue is {dir} {rev}% versus the previous period.',
        );
    }
    return undefined;
}

function lockedRow(card, t) {
    return {
        icon: <LockIcon />,
        tone: CARD_TONE[card.id],
        name: t(card.labelKey, {}, card.fallback),
        value: <span className="tab-locked-hint">{t('hub_locked_hint', {}, 'Upgrade to unlock')}</span>,
        onPress: undefined,
    };
}

// NOTE (mirrors OperationsTab.js's buildDigRow): tappability for a LOCKED
// card is decided entirely inside lockedRow()'s hardcoded `onPress:
// undefined` above — the early return below always wins for `state ===
// 'locked'`, so that field is the one guard the "locked card is not a
// button" assertion actually depends on. The `state === 'active'` ternary
// underneath is a second, independent safeguard for non-locked cards
// (active vs inert) — it never runs for a locked card at all, so mutating
// it alone proves nothing about the locked case. Mutate lockedRow's
// `onPress` field itself to exercise the guard that matters here.
function buildDigRow(card, t, push) {
    if (card.state === 'locked') return lockedRow(card, t);
    const Icon = CARD_ICON[card.id];
    return {
        icon: <Icon />,
        tone: CARD_TONE[card.id],
        name: t(card.labelKey, {}, card.fallback),
        onPress: card.state === 'active' ? () => push(card.id) : undefined,
    };
}

export default function InsightsTab() {
    ensureKitCss();
    ensureTabCss();
    const { t, language } = useLanguage();
    const { getCurrency } = useAuth() || {};
    const { push } = useNav();
    const [preset, setPreset] = useRangePreset();
    const { status, data, reload } = useInsightsData();
    const cards = useHubCards('insights');

    const pillOptions = PRESETS.map((p) => ({ id: p.id, label: t(p.labelKey, {}, p.fallback) }));

    const header = <h1 className="tab-title">{t('tab_insights', {}, 'Insights')}</h1>;

    if (status === 'loading') {
        return (
            <div className="tab-wrap">
                {header}
                <div data-testid="insights-skeleton" aria-hidden="true">
                    <div className="tab-skel" style={{ height: 150 }} />
                    <div className="tab-skel" style={{ height: 100 }} />
                    <div className="tab-skel" style={{ height: 220 }} />
                </div>
            </div>
        );
    }

    if (status === 'error') {
        return (
            <div className="tab-wrap">
                {header}
                <div className="tab-state" data-testid="insights-error">
                    <p className="tab-empty-title">{t('insights_error_title', {}, "Couldn't load insights")}</p>
                    <p className="tab-empty-msg">
                        {t('insights_error_msg', {}, 'Check your connection and try again.')}
                    </p>
                    <button type="button" className="tab-retry" onClick={reload}>
                        {t('retry', {}, 'Retry')}
                    </button>
                </div>
            </div>
        );
    }

    const currency = getCurrency ? getCurrency() : '£';

    // A stale persisted preset id can never reach here (rangeState.js
    // coerces to the default), but rangeForPreset() still throws on an
    // unknown id — degrade the label rather than crash the whole surface.
    let range = null;
    try {
        range = rangeForPreset(preset);
    } catch (e) {
        range = null;
    }
    const rangeLabel = range ? formatRangeLabel(range.start, range.end, language) : undefined;

    const sentence = buildVerdictSentence(data, t);
    const stats = [
        { value: fmtMoney(data.revenue, currency), caption: t('total_revenue', {}, 'revenue') },
    ];
    // Gross margin is omitted entirely when null, never rendered as "null%"
    // or coerced to 0% — correctness point 2.
    if (data.grossMargin != null) {
        stats.push({
            value: `${Number(data.grossMargin).toFixed(1)}%`,
            caption: t('gross_margin', {}, 'gross margin'),
        });
    }

    // data.changes rows already arrive as {id, tone, text} (useInsightsData
    // merges + caps them); the trimmed remainder becomes one final muted
    // row with no chip and no onPress.
    const attentionItems = Array.isArray(data.changes) ? [...data.changes] : [];
    if (data.changesMoreCount > 0) {
        attentionItems.push({
            id: 'more',
            tone: 'muted',
            text: t('and_n_more', { n: data.changesMoreCount }, 'and {n} more'),
        });
    }

    const hasNarrative = typeof data.narrative === 'string' && data.narrative.trim().length > 0;

    return (
        <div className="tab-wrap">
            {header}

            <div className="tab-group">
                <TabPills
                    options={pillOptions}
                    value={preset}
                    onChange={setPreset}
                    ariaLabel={t('date_range', {}, 'Date range')}
                />
            </div>

            <div className="tab-group">
                <VerdictCard
                    label={rangeLabel}
                    sentence={sentence}
                    stats={stats}
                    points={data.sparkline}
                />
            </div>

            <AttentionList items={attentionItems} title={t('what_changed', {}, 'What changed')} />

            {hasNarrative && (
                <>
                    <div className="pk-section-label">{t('the_story', {}, 'The Story')}</div>
                    <div className="tab-group">
                        <GlassCard veil className="pk-verdict" data-testid="insights-narrative">
                            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--c-ink)' }}>
                                {renderBold(data.narrative)}
                            </p>
                            {data.aiGenerated && (
                                <span
                                    className="pk-pill pk-pill-blue"
                                    style={{ marginBlockStart: 10, display: 'inline-flex' }}
                                >
                                    {t('ai_generated', {}, 'AI generated')}
                                </span>
                            )}
                        </GlassCard>
                    </div>
                </>
            )}

            {cards.length === 0 ? (
                <HubEmptyState t={t} testId="insights-empty" />
            ) : (
                <>
                    <div className="pk-section-label">{t('dig_deeper', {}, 'Dig deeper')}</div>
                    <div className="tab-group" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {cards.map((cardEntry) => {
                            if (!CARD_ICON[cardEntry.id]) return null;
                            const row = buildDigRow(cardEntry, t, push);
                            return <CondensedRow key={cardEntry.id} testId={cardEntry.id} {...row} />;
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
